<template>
  <div>
    <div class="calendar-container">
      <div>
        <div
          class="align-items-center justify-content-between flex-wrap !hidden md:flex"
        >
          <FullCalendarZoom v-if="!isWeekView" v-model="zoom" />
          <div v-else></div>
          <div class="calendar-last-updated-at text-body">
            <span
              v-if="isFetching"
              class="calendar-updating"
              data-cy="schedule-updating"
            >
              <span class="calendar-updating-spinner"></span>Updating…
            </span>
            <template v-else>
              <b>Last time updated:</b> {{ lastUpdateLabel }}
            </template>
          </div>
        </div>
        <FullCalendarTimeline
          ref="calendar"
          :options="fullCalendarOptions"
          :class-names="['orders-calendar', { 'mobile-calendar': isMobile }]"
          pre-work-enabled
          :zoom="zoom"
          @open-edit-modal="openModal"
          @refetch-events="refetchEvents"
          @open-create-appointment-modal="openCreateModal"
          @validation-error="showValidationError"
          @resize-appointment="handleResizeAppointment"
          @switch-to-day="handleSwitchToDay"
          @loading-change="onLoadingChange"
        />
      </div>
    </div>
    <span class="shortcut-label">Use shortcut Alt + R to reload data</span>
    <appointment-create-modal
      v-if="showCreateModal"
      :scheduled_time="createModalData.scheduled_time"
      :technician_id="createModalData.technician_id"
      @hide-modal="closeCreateModal"
      @created="closeCreateModal"
    ></appointment-create-modal>
    <pinned-reason-modal
      v-if="pinToTechId"
      :technician-name="pinToTechName"
      :technician-id="pinToTechId"
      :appointment-id="pinApptId"
      :pending-time-payload="pendingTimePayload"
      :multi-tech-ids="pendingMultiTechIds"
      @hide-modal="handlePinnedModalClose"
    ></pinned-reason-modal>
    <reschedule-notify-modal
      v-if="pendingReschedulePayload"
      :pending-payload="pendingReschedulePayload"
      @hide-modal="handleRescheduleModalClose"
    />
    <multi-tech-unassign-modal
      v-if="pendingMultiTechUnassign"
      :pending-payload="pendingMultiTechUnassign"
      :removed-tech-name="pendingMultiTechUnassign.removedTechName"
      @hide-modal="handleMultiTechUnassignModalClose"
    />
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapActions, mapGetters, mapState } from "vuex";
import { EventBus } from "@/lib/event_bus.js";
import { isMobileScreenSize } from "@/lib/helpers.js";
import { trackMobileWebEvent } from "@/lib/pendo";
import { syncStartParam } from "@/lib/scheduleStartParam";
import { getQueryVariables } from "@/lib/url.js";

import AppointmentCreateModal from "@/AppointmentCreateModal/AppointmentCreateModal.vue";
import CONSTANTS from "@/Constants";
import { currentTenantTimezone } from "../lib/helpers.js";
import FullCalendarTimeline from "./FullCalendarTimeline.vue";
import FullCalendarZoom from "./FullCalendarZoom.vue";
import MultiTechUnassignModal from "./MultiTechUnassignModal.vue";
import PinnedReasonModal from "./PinnedReasonModal.vue";
import RescheduleNotifyModal from "./RescheduleNotifyModal.vue";
const { SCHEDULE_DEFAULT_ZOOM } = CONSTANTS;

const PLANNER_REFETCH_DELAY = 5 * 1000;
const CALENDAR_REFRESH_INTERVAL = 60 * 1000;

export default {
  components: {
    FullCalendarTimeline,
    AppointmentCreateModal,
    MultiTechUnassignModal,
    PinnedReasonModal,
    RescheduleNotifyModal,
    FullCalendarZoom
  },
  data() {
    return {
      calendar: null,
      timezone: currentTenantTimezone,
      updatedAt: new Date(),
      isFetching: false,
      plannerTimeout: null,
      calendarInterval: null,
      isUnmounted: false,
      showCreateModal: false,
      createModalData: {
        scheduled_time: "",
        technician_id: null
      },
      pinToTechId: null,
      pinApptId: null,
      pinToTechName: "",
      pendingTimePayload: null,
      pendingReschedulePayload: null,
      pendingMultiTechIds: null,
      pendingMultiTechUnassign: null,
      zoom: SCHEDULE_DEFAULT_ZOOM,
      lastGoodFetch: {},
      lastFetchFailureNoticeAt: 0
    };
  },
  computed: {
    ...mapState(["tenant", "fetchingTenant"]),
    ...mapGetters(["featureEnabledForTenant", "scheduleWeekViewActive"]),
    ...mapState({
      department_ids({ scheduleBoardFilters }) {
        return scheduleBoardFilters.department_ids;
      },
      start({ scheduleBoardFilters }) {
        return scheduleBoardFilters.start;
      },
      fullCalendarOptions() {
        // The board date is URL-driven: no start param means today
        const { start } = getQueryVariables();
        const initialDate = start || new Date();
        return {
          initialDate,
          initialView: this.effectiveViewType,
          timeZone: this.timezone,
          resources: $.debounce(
            100,
            (fetchInfo, successCallback, failureCallback) => {
              return this.fetchData(
                successCallback,
                fetchInfo.start,
                fetchInfo.end,
                "appointments_calendar/resources.json",
                null,
                null,
                failureCallback
              );
            }
          ),
          events: $.debounce(
            50,
            (fetchInfo, successCallback, failureCallback) => {
              const wrappedCb = (data) => {
                let events = this.isWeekView
                  ? this.filterWeekViewEvents(data)
                  : data;
                if (this.isMultiTechEnabled) {
                  events = this.expandMultiTechEvents(events);
                }
                successCallback(events);
              };
              return this.fetchData(
                wrappedCb,
                fetchInfo.start,
                fetchInfo.end,
                "appointments_calendar/events.json",
                "&status=all",
                "events",
                failureCallback
              );
            }
          ),
          eventAllow: (dropInfo, draggedEvent) => {
            if (this.isEventDragLocked(draggedEvent)) return false;
            // Week view newly enables cross-day drops; there is no backend
            // guard against rescheduling into the past, so block it here.
            if (
              this.isWeekView &&
              moment(dropInfo.start).isBefore(moment().startOf("day"))
            ) {
              return false;
            }
            return true;
          },
          eventDrop: this.handleEventDrop
        };
      },
      isWeekView() {
        return this.scheduleWeekViewActive;
      },
      effectiveViewType() {
        return this.isWeekView ? "resourceTimelineWeek" : "resourceTimelineDay";
      },
      lastUpdateLabel() {
        return moment(this.updatedAt).format("h:mm:ss a");
      },
      isMobile() {
        return isMobileScreenSize();
      },
      isMultiTechEnabled() {
        return this.featureEnabledForTenant("multiple_technicians_ff_enabled");
      }
    })
  },
  watch: {
    department_ids(newValue, oldValue) {
      if (newValue !== oldValue) {
        const api = this.getCalendarApi();
        api?.refetchResources?.();
        this.refetchEvents();
      }
    },
    effectiveViewType(newView, oldView) {
      if (newView === oldView) return;
      const api = this.getCalendarApi();
      api?.changeView?.(newView, this.start || undefined);
    }
  },
  created() {
    if (!this.tenant && !this.fetchingTenant) {
      this.fetchTenant();
    }
  },
  mounted() {
    const { appointment_id } = getQueryVariables();
    if (appointment_id) {
      this.openModal(appointment_id);
    }
    this.pollData();
    this.refetchEvents = this.refetchEvents.bind(this);
    document.addEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$on("refresh-schedule-board", this.refetchEvents);
    EventBus.$on("schedule-board-date-change", this.handleDateChange);
  },
  beforeUnmount() {
    this.isUnmounted = true;
    clearTimeout(this.plannerTimeout);
    clearInterval(this.calendarInterval);
    document.removeEventListener("keydown", this.handleKeyboardShortcut);
    EventBus.$off("refresh-schedule-board", this.refetchEvents);
    EventBus.$off("schedule-board-date-change", this.handleDateChange);
  },
  methods: {
    ...mapActions([
      "fetchTenant",
      "updateAppointmentEvent",
      "assignAppointmentEvent",
      "setScheduleBoardFilters"
    ]),
    filterWeekViewEvents(events) {
      // The proportional week timeline positions travel and pre-work at their
      // real times, so they're useful — keep them. Only the end-of-day
      // "schedule" marker (a boundary marker, not a real time block) is dropped.
      return events.filter((e) => !e.schedule);
    },
    handleSwitchToDay(date) {
      this.setScheduleBoardFilters({ view: "day", start: date });
      // FullCalendar emits Dates whose UTC fields carry the calendar-local
      // day, so read the day in UTC mode when syncing the URL
      syncStartParam(moment.utc(date), { replace: true });
      this.$nextTick(() => {
        const api = this.getCalendarApi();
        api?.gotoDate?.(date);
      });
    },
    onLoadingChange(isLoading) {
      this.isFetching = isLoading;
    },
    getCalendarApi() {
      if (this.isUnmounted) return null;
      const calendar = this.$refs.calendar;
      // `api` is a method on FullCalendarTimeline (a live getApi() read, not a
      // cached computed — see the note there re vuejs/core#12341).
      return calendar?.api?.() ?? null;
    },
    handleDateChange(date) {
      const api = this.getCalendarApi();
      if (api?.gotoDate) {
        api.gotoDate(date);
      }
    },
    handleKeyboardShortcut(e) {
      // Alt + r to refetch events
      if (e.altKey && e.which === 82) {
        this.refetchEvents();
      }
    },
    async fetchData(
      callback,
      start,
      end,
      url,
      additionalParams,
      resultKey,
      failureCallback
    ) {
      const { department_ids } = getQueryVariables();
      let queryParams = `?start=${moment(start).format()}&end=${moment(
        end
      ).format()}&_=${Date.now()}`;
      if (department_ids) {
        queryParams = `${queryParams}&department_ids=${department_ids}`;
      }
      if (additionalParams) {
        queryParams += additionalParams;
      }
      // Cache per source + visible range + department filter so a restored
      // payload always matches what the failed refetch was asking for.
      const cacheKey = `${url}|${moment(start).format()}|${moment(
        end
      ).format()}|${department_ids || ""}`;
      try {
        const res = await $.ajax("" + url + queryParams);
        if (this.isUnmounted) return;
        this.updatedAt = new Date();
        const data = resultKey ? res[resultKey] : res;
        const normalizedData =
          resultKey === "events" && Array.isArray(data)
            ? this.lockNonPendingEvents(data)
            : data;
        this.lastGoodFetch[cacheKey] = normalizedData;
        if (callback) callback(normalizedData);
      } catch {
        if (this.isUnmounted) return;
        // A failed refetch (e.g. timeout under DB saturation) must not blank
        // the board — that hides real appointments and causes overbooking.
        // Re-feed the last good payload for this exact range when we have one;
        // otherwise let FullCalendar handle the failure for a range we never
        // successfully loaded.
        const cached = this.lastGoodFetch[cacheKey];
        if (cached !== undefined && callback) {
          callback(cached);
        } else if (failureCallback) {
          failureCallback(new Error("Schedule fetch failed"));
        }
        this.notifyFetchFailure();
      }
    },
    notifyFetchFailure() {
      // Throttle so a burst of failed refetches surfaces one notice, not many.
      const now = Date.now();
      if (now - this.lastFetchFailureNoticeAt < 30000) return;
      this.lastFetchFailureNoticeAt = now;
      window.notify?.error?.(
        "Couldn't refresh the schedule. Showing the last loaded view — it may be out of date."
      );
    },
    eventStatus(event) {
      const props = event?.extendedProps || {};
      const status = props.status || event?.status;
      if (typeof status === "string") return status.toLowerCase();
      return status;
    },
    isEventDragLocked(event) {
      const props = event?.extendedProps || {};

      if (props.calendar_event) return true;
      // Travel/pre-work rows represent non-pending appointment stages and should never be dragged.
      if (props.travel || props.pre_work) return true;
      // Multi-tech clones lock per-tech: a still-assigned tech stays draggable
      // for reassignment even when a crewmate is running.
      if (props._isMultiTechClone) return props._techStatus !== "assigned";
      const status = this.eventStatus(event);
      if (status && status !== "pending") return true;
      return false;
    },
    lockNonPendingEvents(events) {
      return events.map((event) => {
        if (!this.isEventDragLocked(event)) return event;

        return {
          ...event,
          editable: false,
          startEditable: false,
          durationEditable: false,
          resourceEditable: false
        };
      });
    },
    expandMultiTechEvents(events) {
      const expanded = [];
      for (const event of events) {
        const techIds = event.appointment_technician_ids;
        if (techIds && techIds.length > 1) {
          const techStatuses = event.technician_statuses || {};
          const isTravelEvent = event.travel === true;
          const isPreworkEvent = event.pre_work === true;
          for (const techId of techIds) {
            const techData = techStatuses[techId];
            if (isTravelEvent) {
              if (!techData?.dispatch_time) continue;
              expanded.push(this.buildTravelClone(event, techId, techData));
            } else if (isPreworkEvent) {
              if (!techData?.accepted_time) continue;
              expanded.push(this.buildPreworkClone(event, techId, techData));
            } else {
              expanded.push(
                this.buildAppointmentClone(event, techId, techData, techIds)
              );
            }
          }
        } else {
          expanded.push(event);
        }
      }
      return expanded;
    },
    buildAppointmentClone(event, techId, techData, techIds) {
      const techStatus = techData?.technician_status;
      // Per-tech draggability: a still-assigned tech can be reassigned to
      // another row even when a crewmate is already running. The time/window
      // stays locked unless the whole appointment is still pending, because all
      // techs share one scheduled_time and a running crewmate already started.
      const perTechAssigned = techStatus === "assigned";
      const apptPending = event.status === "pending";
      const clone = {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        color: techData?.color || event.color,
        editable: perTechAssigned,
        resourceEditable: perTechAssigned,
        startEditable: perTechAssigned && apptPending,
        durationEditable: perTechAssigned && apptPending,
        extendedProps: {
          ...event.extendedProps,
          _appointmentId: event.id,
          _allTechIds: techIds,
          _isMultiTechClone: true,
          _techStatus: techStatus
        }
      };
      if (techData) {
        this.applyPerTechTimes(clone, techData);
      }
      return clone;
    },
    buildTravelClone(event, techId, techData) {
      return {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        start: techData.dispatch_time,
        end: techData.arrival_time || new Date().toISOString(),
        extendedProps: {
          ...event.extendedProps,
          _isMultiTechClone: true
        }
      };
    },
    buildPreworkClone(event, techId, techData) {
      return {
        ...event,
        id: `${event.id}_mt_${techId}`,
        resourceId: techId,
        start: techData.accepted_time,
        end: techData.dispatch_time || new Date().toISOString(),
        extendedProps: {
          ...event.extendedProps,
          _isMultiTechClone: true
        }
      };
    },
    applyPerTechTimes(clone, techData) {
      const status = techData.technician_status;
      if (
        (status === "arrived" || status === "departed") &&
        techData.arrival_time
      ) {
        clone.start = techData.arrival_time;
      }
      if (status === "departed" && techData.departure_time) {
        clone.end = techData.departure_time;
      }
      // Ensure minimum visual duration
      if (clone.start && clone.end) {
        const startMs = new Date(clone.start).getTime();
        const endMs = new Date(clone.end).getTime();
        const minDuration = 15 * 60 * 1000;
        if (endMs - startMs < minDuration) {
          clone.end = new Date(startMs + minDuration).toISOString();
        }
      }
    },
    handleEventDrop(info) {
      // Hide all tooltips
      tippy.hideAll();
      const oldResource = info.oldResource;
      const newResource = info.newResource;
      const isMultiTech = info.event.extendedProps?._isMultiTechClone === true;
      const appointmentId = this.extractAppointmentId(info.event.id);
      if (this.isEventDragLocked(info.event)) {
        info.revert?.();
        return;
      }

      // A multi-tech clone past pending can be reassigned but not rescheduled
      // (one shared scheduled_time, and a crewmate has already started). Ignore
      // any time delta so we never POST a reschedule the backend rejects with a
      // 409 — this holds even if a drag slips past the per-tech startEditable lock.
      const timeReschedulable =
        !isMultiTech || this.eventStatus(info.event) === "pending";
      const timeChanged =
        timeReschedulable &&
        info.event.start?.getTime() !== info.oldEvent.start?.getTime();

      const technicianChanged =
        !!oldResource && !!newResource && oldResource.id !== newResource.id;

      let timePayload = null;
      if (timeChanged) {
        const newStart = moment(info.event.start);
        const newEnd = moment(info.event.end);
        timePayload = {
          arrival_window_start: newStart.format(),
          arrival_window_end: newEnd.format(),
          scheduled_time: newStart.clone().add(1, "hour").format()
        };
      }

      // Ignore null resources
      if (oldResource && newResource) {
        // Safely check for unassigned - department.id will be null/undefined for unassigned row
        const newDept = newResource.extendedProps?.department;
        const toUnassigned = !newDept?.id;

        if (technicianChanged && isMultiTech) {
          const allTechIds = info.event.extendedProps._allTechIds;
          const sourceTechId = parseInt(oldResource.id, 10);
          const targetTechId = toUnassigned
            ? null
            : parseInt(newResource.id, 10);

          // Build new roster: remove source tech, add target tech (if not already present)
          const newRoster = allTechIds.filter((id) => id !== sourceTechId);
          if (targetTechId && !newRoster.includes(targetTechId)) {
            newRoster.push(targetTechId);
          }

          if (toUnassigned) {
            this.pendingMultiTechUnassign = {
              appointmentId,
              technicianProfileIds: newRoster,
              timePayload,
              removedTechName: oldResource.title
            };
          } else {
            // Show pinned reason modal for multi-tech swap
            this.pinToTechName = newResource.title;
            this.pinToTechId = targetTechId;
            this.pinApptId = appointmentId;
            this.pendingTimePayload = timePayload;
            this.pendingMultiTechIds = newRoster;
          }
        } else if (technicianChanged) {
          // Use null for unassigned, otherwise parse the tech ID
          const newTechId = toUnassigned ? null : parseInt(newResource.id, 10);
          const newTechName = newResource.title;

          if (toUnassigned) {
            this.pendingReschedulePayload = {
              appointmentId,
              technician_profile_id: newTechId,
              ...timePayload
            };
          } else {
            // Show pinned reason modal for assigning to a technician
            this.pinToTechName = newTechName;
            this.pinToTechId = newTechId;
            this.pinApptId = appointmentId;
            this.pendingTimePayload = timePayload;
          }
        } else if (timeChanged) {
          this.pendingReschedulePayload = {
            appointmentId,
            ...timePayload
          };
        } else {
          // No actual change - reset UI
          this.refetchEvents();
        }
      } else if (timeChanged) {
        this.pendingReschedulePayload = {
          appointmentId,
          ...timePayload
        };
      }

      // Derived after the chain rather than set inside it, so a drop path added
      // later is instrumented by default. timePayload is the payload itself, so
      // this reports what was sent: a multi-tech clone past pending has its time
      // delta suppressed above and must not claim a time change.
      let change = null;
      if (technicianChanged) {
        change = timePayload ? "technician_and_time" : "technician";
      } else if (timePayload) {
        change = "time";
      }

      if (change) {
        trackMobileWebEvent("schedule_board_drag", {
          board: "v1",
          change,
          appointment_id: appointmentId,
          multi_tech: isMultiTech
        });
      }
    },
    refetchEvents() {
      const api = this.getCalendarApi();
      if (!api?.refetchEvents) return;
      api.refetchEvents();
      this.updatedAt = new Date();
    },
    waitForPlannerUpdates() {
      // Refetch events after planner updates
      clearTimeout(this.plannerTimeout);
      this.plannerTimeout = setTimeout(
        () => this.refetchEvents(),
        PLANNER_REFETCH_DELAY
      );
    },
    extractAppointmentId(id) {
      const rawId = String(id);
      return rawId.includes("_mt_")
        ? parseInt(rawId.split("_mt_")[0], 10)
        : parseInt(rawId, 10);
    },
    openModal(id) {
      try {
        const appointmentId = this.extractAppointmentId(id);
        EventBus.$emit("open-appointment-drawer", appointmentId);
      } catch (e) {
        console.error(e);
      }
    },
    openCreateModal(data) {
      this.createModalData = {
        scheduled_time: data.scheduled_time,
        technician_id: data.technician_id
      };
      this.showCreateModal = true;
    },
    closeCreateModal() {
      this.showCreateModal = false;
      this.createModalData = {
        scheduled_time: "",
        technician_id: null
      };
      this.refetchEvents();
    },
    pollData() {
      clearInterval(this.calendarInterval);
      this.calendarInterval = setInterval(() => {
        this.refetchEvents();
      }, CALENDAR_REFRESH_INTERVAL);
    },
    handlePinnedModalClose() {
      this.pinToTechName = null;
      this.pinToTechId = null;
      this.pinApptId = null;
      this.pendingTimePayload = null;
      this.pendingMultiTechIds = null;
      this.refetchEvents();
    },
    handleResizeAppointment(payload) {
      return this.updateAppointmentEvent(payload).then(() => {
        window.notify.success("Appointment updated");
        this.refetchEvents();
      });
    },
    handleRescheduleModalClose() {
      this.pendingReschedulePayload = null;
      this.refetchEvents();
    },
    handleMultiTechUnassignModalClose() {
      this.pendingMultiTechUnassign = null;
      this.refetchEvents();
    },
    showValidationError(message) {
      // Show validation error message using the same pattern as scheduler 2.0
      window.notify.error(message);
    }
  }
};
</script>

<style lang="scss" scoped>
.shortcut-label {
  font-style: italic;
  margin-top: 10px;
  color: var(--gray);
  font-size: 0.8em;
  display: inline-block;
  position: absolute;
  bottom: -20px;
}
.card {
  padding: 15px;
}

.calendar-container {
  position: relative;
  padding-top: 0 !important;
  margin-top: -20px;
  .calendar-last-updated-at {
    color: var(--font-body-color);
    font-size: 0.8em;
    height: 28px;
  }
  .calendar-updating {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: var(--primary-color, #2563eb);
    font-weight: 600;
  }
  .calendar-updating-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid #bfdbfe;
    border-top-color: #2563eb;
    border-radius: 50%;
    animation: calendar-updating-spin 0.7s linear infinite;
  }
}

@keyframes calendar-updating-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .calendar-updating-spinner {
    animation: none;
  }
}

@media (max-width: 600px) {
  .calendar-last-updated-at {
    left: 18px;
    top: 0px !important;
    font-size: 0.8em !important;
  }
}
</style>
